import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/lib/auth';
import { useQuery, useMutation } from '@tanstack/react-query';
import { subscriptionsApi } from '@/lib/api/subscriptions';
import { supportApi } from '@/lib/api/support';
import { Loading } from '@/components/Loading';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { SubscriptionPlanType, SubscriptionStatus, type SubscriptionPlanDto } from '@paladin-monorepo/common';
import { Check, ExternalLink, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import { formatPrice } from '@/utils/formatPrice';
import { getAppFormatLocale } from '@/utils/locale';
import { getHttpErrorMessage } from '@/lib/httpErrorMessage';

function StatusBadge({ status, cancelAtPeriodEnd }: { status: string; cancelAtPeriodEnd?: boolean }) {
  const { t } = useTranslation();

  if (cancelAtPeriodEnd) {
    return <Badge variant="warning">{t('subscription.status.canceling')}</Badge>;
  }

  const variants: Record<string, 'success' | 'warning' | 'error' | 'secondary'> = {
    [SubscriptionStatus.ACTIVE]: 'success',
    [SubscriptionStatus.TRIALING]: 'info' as any,
    [SubscriptionStatus.PAST_DUE]: 'warning',
    [SubscriptionStatus.CANCELED]: 'error',
    [SubscriptionStatus.UNPAID]: 'error',
    [SubscriptionStatus.INCOMPLETE]: 'warning',
    [SubscriptionStatus.PAUSED]: 'secondary',
  };

  return (
    <Badge variant={variants[status] ?? 'secondary'}>
      {t(`subscription.status.${status}`, status)}
    </Badge>
  );
}

export function SubscriptionTab() {
  const { t } = useTranslation();
  const { user, isLoading: userLoading } = useAuth({ middleware: 'auth', tmp: 'SubscriptionTab' });

  const { data: plans, isLoading: plansLoading } = useQuery({
    queryKey: ['subscriptions', 'plans', user?.currentAccountId, user?.account?.accountType],
    queryFn: () => subscriptionsApi.getPlans(),
    enabled: !!user?.currentAccountId,
  });

  const { data: current, isLoading: currentLoading } = useQuery({
    queryKey: ['subscriptions', 'current'],
    queryFn: () => subscriptionsApi.getCurrentSubscription(),
    enabled: !!user?.currentAccountId,
  });

  const checkoutMutation = useMutation({
    mutationFn: ({ planId, priceId }: { planId: string; priceId: string }) =>
      subscriptionsApi.createCheckoutSession({ planId, priceId }),
    onSuccess: (data) => {
      window.location.href = data.url;
    },
    onError: (error) => {
      toast.error(getHttpErrorMessage(error, t('subscription.checkoutError')));
    },
  });

  const portalMutation = useMutation({
    mutationFn: () => subscriptionsApi.createPortalSession(),
    onSuccess: (data) => {
      window.location.href = data.url;
    },
    onError: (error) => {
      toast.error(getHttpErrorMessage(error, t('subscription.portalError')));
    },
  });

  const [enterpriseDialogOpen, setEnterpriseDialogOpen] = useState(false);

  if (userLoading || plansLoading || currentLoading) {
    return <Loading />;
  }

  const currentPlan = current?.plan;
  const billing = current?.billing;
  const hasBilling = billing &&
    billing.status !== SubscriptionStatus.CANCELED &&
    billing.status !== SubscriptionStatus.NONE;

  const showManageStripeSubscription =
    hasBilling && Boolean(billing.stripeSubscriptionId);

  const isEnterprise = currentPlan?.type === SubscriptionPlanType.ENTERPRISE;
  const isFree = currentPlan?.type === SubscriptionPlanType.FREE;

  const availablePlans = plans ?? [];
  const currentPlanSortOrder = currentPlan?.sortOrder ?? 0;

  return (
    <div className="space-y-6">
      {/* Current Plan Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>{t('subscription.currentPlan')}</CardTitle>
            {hasBilling && (
              <StatusBadge
                status={billing.status}
                cancelAtPeriodEnd={billing.cancelAtPeriodEnd}
              />
            )}
            {isFree && <Badge variant="secondary">{t('subscription.free')}</Badge>}
          </div>
          <CardDescription>
            {currentPlan?.name ?? t('subscription.noPlan')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isEnterprise && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Building2 className="h-4 w-4" />
              {t('subscription.enterpriseManaged')}
            </div>
          )}
          {isFree && (
            <p className="text-sm text-muted-foreground">
              {t('subscription.freeDescription')}
            </p>
          )}
          {hasBilling && billing.currentPeriodEnd && (
            <div className="text-sm text-muted-foreground">
              {t('subscription.nextBilling')}:{' '}
              <span className="font-medium text-foreground">
                {new Date(billing.currentPeriodEnd).toLocaleDateString(getAppFormatLocale())}
              </span>
            </div>
          )}
        </CardContent>
        {showManageStripeSubscription && (
          <CardFooter>
            <Button
              variant="outline"
              onClick={() => portalMutation.mutate()}
              disabled={portalMutation.isPending}
            >
              <ExternalLink className="mr-2 h-4 w-4" />
              {t('subscription.manageSubscription')}
            </Button>
          </CardFooter>
        )}
      </Card>

      {/* Available Plans */}
      {availablePlans.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold mb-4">
            {hasBilling
              ? t('subscription.changePlan')
              : t('subscription.choosePlan')}
          </h3>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {availablePlans.map((plan) => (
              <PlanCard
                key={plan.id}
                plan={plan}
                isCurrentPlan={currentPlan?.id === plan.id}
                currentPlanSortOrder={currentPlanSortOrder}
                hasHigherPlan={!!hasBilling}
                onSelect={(priceId) => checkoutMutation.mutate({ planId: plan.id, priceId })}
                onEnterprise={() => setEnterpriseDialogOpen(true)}
                isLoading={checkoutMutation.isPending}
              />
            ))}
          </div>
        </div>
      )}

      <EnterpriseContactDialog
        open={enterpriseDialogOpen}
        onOpenChange={setEnterpriseDialogOpen}
      />
    </div>
  );
}

function PlanCard({
  plan,
  isCurrentPlan,
  currentPlanSortOrder,
  hasHigherPlan,
  onSelect,
  onEnterprise,
  isLoading,
}: {
  plan: SubscriptionPlanDto;
  isCurrentPlan: boolean;
  currentPlanSortOrder: number;
  hasHigherPlan: boolean;
  onSelect: (priceId: string) => void;
  onEnterprise: () => void;
  isLoading: boolean;
}) {
  const { t } = useTranslation();
  const isFree = plan.type === SubscriptionPlanType.FREE;
  const isEnterprise = plan.type === SubscriptionPlanType.ENTERPRISE;
  const monthlyPrice = (plan.prices ?? []).find((p) => p.interval === 'month');
  const features = plan.features ?? [];
  const isDowngrade = !isCurrentPlan && isFree && (hasHigherPlan || currentPlanSortOrder > plan.sortOrder);

  return (
    <Card className={isCurrentPlan ? 'border-primary ring-1 ring-primary' : ''}>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          {plan.name}
          {isCurrentPlan && (
            <Badge variant="default">{t('subscription.current')}</Badge>
          )}
        </CardTitle>
        {plan.description && (
          <CardDescription>{plan.description}</CardDescription>
        )}
      </CardHeader>
      <CardContent className="flex-1">
        <div className="mb-4">
          {isFree ? (
            <span className="text-3xl font-bold">{t('subscription.free')}</span>
          ) : isEnterprise ? (
            <span className="text-3xl font-bold">{t('subscription.enterprisePrice')}</span>
          ) : monthlyPrice ? (
            <>
              <span className="text-3xl font-bold">
                {formatPrice(monthlyPrice.unitAmount / 100, monthlyPrice.currency)}
              </span>
              <span className="text-muted-foreground">/{t('subscription.month')}</span>
            </>
          ) : null}
        </div>
        {features.length > 0 && (
          <ul className="space-y-2">
            {features.map((feature, idx) => (
              <li key={idx} className="flex items-start gap-2 text-sm">
                <Check className="h-4 w-4 mt-0.5 text-primary shrink-0" />
                <span>{feature}</span>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
      <CardFooter>
        <PlanCardButton
          isCurrentPlan={isCurrentPlan}
          isFree={isFree}
          isEnterprise={isEnterprise}
          isDowngrade={isDowngrade}
          monthlyPrice={monthlyPrice}
          onSelect={onSelect}
          onEnterprise={onEnterprise}
          isLoading={isLoading}
        />
      </CardFooter>
    </Card>
  );
}

function PlanCardButton({
  isCurrentPlan,
  isFree,
  isEnterprise,
  isDowngrade,
  monthlyPrice,
  onSelect,
  onEnterprise,
  isLoading,
}: {
  isCurrentPlan: boolean;
  isFree: boolean;
  isEnterprise: boolean;
  isDowngrade: boolean;
  monthlyPrice: { id: string } | undefined;
  onSelect: (priceId: string) => void;
  onEnterprise: () => void;
  isLoading: boolean;
}) {
  const { t } = useTranslation();

  if (isCurrentPlan) {
    return (
      <Button variant="outline" disabled className="w-full">
        {t('subscription.currentPlanButton')}
      </Button>
    );
  }

  if (isEnterprise) {
    return (
      <Button className="w-full" variant="outline" onClick={onEnterprise}>
        {t('subscription.contactSales')}
      </Button>
    );
  }

  if (isFree && isDowngrade) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="w-full">
            <Button variant="outline" disabled className="w-full pointer-events-none">
              {t('subscription.selectPlan')}
            </Button>
          </span>
        </TooltipTrigger>
        <TooltipContent>
          {t('subscription.cancelCurrentFirst')}
        </TooltipContent>
      </Tooltip>
    );
  }

  return (
    <Button
      className="w-full"
      onClick={() => monthlyPrice && onSelect(monthlyPrice.id)}
      disabled={isLoading || !monthlyPrice}
    >
      {t('subscription.selectPlan')}
    </Button>
  );
}

function EnterpriseContactDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const [subject, setSubject] = useState(t('subscription.enterpriseDialog.defaultSubject'));
  const [message, setMessage] = useState('');

  const mutation = useMutation({
    mutationFn: () => supportApi.createTicket({ subject, message }),
    onSuccess: () => {
      toast.success(t('subscription.enterpriseDialog.success'));
      onOpenChange(false);
      setMessage('');
    },
    onError: (error) => {
      toast.error(getHttpErrorMessage(error, t('subscription.enterpriseDialog.error')));
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('subscription.enterpriseDialog.title')}</DialogTitle>
          <DialogDescription>
            {t('subscription.enterpriseDialog.description')}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="support-subject">{t('subscription.enterpriseDialog.subject')}</Label>
            <Input
              id="support-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="support-message">{t('subscription.enterpriseDialog.message')}</Label>
            <Textarea
              id="support-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              placeholder={t('subscription.enterpriseDialog.messagePlaceholder')}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('generic.cancel')}
          </Button>
          <Button
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending || !subject.trim() || !message.trim()}
          >
            {t('subscription.enterpriseDialog.send')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
